'use client';

/**
 * ChargeGooglePayCheckoutFlow Component
 *
 * Headless processor for Google Pay payments via Checkout.com Flow SDK.
 * Reports status via callbacks; renders only a hidden container for SDK mounting.
 * Does NOT render visible UI — parent uses ChargeStatusDisplay for display.
 *
 * Why the work happens up front (MBW-2436):
 * The Google Pay sheet must open from inside the browser's user-gesture window.
 * Previously the purchase, payment session, SDK init and Flow mount all ran
 * *after* the click, then a synthetic click was fired ~1s later — by which time
 * the gesture had expired, so the sheet opened late or only after several
 * clicks. Now all that async work runs on mount (as soon as a GP method is
 * selected). When ready, the processor exposes a synchronous trigger via
 * `nativeTriggerRef`; the parent calls it directly in the user's click handler,
 * so the synthetic click on the (already mounted) Flow GPay button runs inside
 * the live gesture and the sheet opens on the first click.
 *
 * Lifecycle:
 * - Mounted when a GP payment method is selected (stays mounted until the method
 *   changes — see ProcessorElement).
 * - On mount (once the SDK script is ready): getProcessingData → createPurchase
 *   → createPaymentSession → Flow mount() — all in the background, silently.
 * - The session is created once per `amount`; if the amount changes the previous
 *   flow is cleaned up and a fresh session is prepared.
 * - `onReady` fires when the Flow GPay button is mounted; the processor sets the
 *   trigger and the parent reveals/enables the Pay button.
 * - The user clicks Pay → parent invokes the trigger → the hidden GPay button is
 *   clicked synchronously → onSubmit/onAuthorized → onPaymentCompleted → poll.
 * - 8s dismissal timeout (armed when the user starts the GP flow) calls onDismiss
 *   but keeps the flow alive so a late authorization still completes.
 *
 * Note: This component MUST render a hidden DOM container because the
 * Checkout.com Flow SDK requires a DOM element to mount into.
 */

import React, {
  useState, useEffect, useRef, useCallback
} from 'react';
import { isSafari, isMobile } from 'react-device-detect';
import { usePurchaseLog } from '../../config/PurchaseFlowConfigProvider';
import { usePurchaseAnalytics } from '../../analytics';
import { getClientPlatformForAPI, browserContext } from '../../utils/platform';
import { useGatewayScript } from '../hooks/useGatewayScript';
import {
  createPaymentSession,
  processPaymentSessionPayment,
  getPurchaseStatus,
  createPurchase,
  getProcessingData,
  ApiError
} from '../../api';
import { withCaptchaPromise } from '../../utils/withCaptchaPromise';
import { poll } from '../../utils/polling';
import { PAYMENT_ERROR_TYPES } from '../../utils/constants';
import type { ChargeProcessorProps } from '../../types';

/** aria-label of the Google Pay button the Checkout.com Flow SDK renders. */
const GPAY_BUTTON_SELECTOR = 'button[aria-label="Pay with GPay"]';

/**
 * Test if popups are blocked (Safari specific issue)
 */
const testPopupBlocked = async (): Promise<boolean> => new Promise((resolve) => {
  setTimeout(() => {
    const testWin = window.open('about:blank', '_blank', 'width=1,height=1');
    if (!testWin) {
      resolve(true);
      return;
    }
    testWin.close();
    resolve(false);
  }, 300);
});

/** Timeout (ms) after which we assume the user dismissed the GP sheet if no onAuthorized. Matches old google_pay/index.js. */
const DISMISSAL_TIMEOUT_MS = 8000;

/**
 * Timeout (ms) after Flow mount() for the GP button to become ready (onReady).
 * If it elapses the button never rendered — e.g. Safari blocked
 * pay.google.com's pay.js — so we stop the infinite spinner and let the user's
 * click surface the error instead (MBW-2436).
 */
const BUTTON_READY_TIMEOUT_MS = 10000;

type PaymentSessionConfig = {
  payment_session: {
    id: string;

    _links?: any;
    payment_session_token?: string;
    payment_session_secret?: string;
  };
  public_key: string;
  environment: string;
};

export const ChargeGooglePayCheckoutFlow: React.FC<ChargeProcessorProps> = ({
  amount,
  withBonus = false,
  paymentSourceId,
  purchaseType,
  advisorId,
  conversationType,
  clickSource,
  purchaseContext,
  fxRate,
  currency,
  riskModel,
  riskModelConfig,
  withCaptcha,
  onStatusChange,
  onSuccess,
  onError,
  onDismiss,
  onReady,
  nativeTriggerRef
}) => {
  const [sessionConfig, setSessionConfig] = useState<PaymentSessionConfig | null>(null);

  const log = usePurchaseLog();
  const analytics = usePurchaseAnalytics();

  // DOM reference for mounting Checkout.com Flow component (REQUIRED for SDK)
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs for values accessed in callbacks
  const userCreditIdRef = useRef<number | null>(null);
  const paymentSessionIdRef = useRef<string | null>(null);
  const successMessageRef = useRef<string | undefined>(undefined);
  const cashbackAmountRef = useRef<string | undefined>(undefined);

  // Refs for external library instances
  const checkoutRef = useRef<any>(null);
  const flowRef = useRef<any>(null);
  const pollingStartedRef = useRef(false);

  // Amount the in-flight/prepared session was created for. Lets us create the
  // session once and reuse it across re-renders, refreshing only if the amount
  // changes (MBW-2436 cleanup requirement).
  const preparedAmountRef = useRef<number | null>(null);

  // 8s dismissal timeout — armed when the user starts the GP flow, cleared in
  // onAuthorized/onError so we don't cancel mid-payment.
  const dismissalTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Watchdog for the GP button rendering after mount(); cleared once onReady fires.
  const readyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load Checkout.com Flow library (eagerly, on mount)
  const { isReady: scriptReady, error: scriptError } = useGatewayScript({
    globalVarName: 'CheckoutWebComponents',
    scriptSrc: 'https://checkout-web-components.checkout.com/index.js',
    checkFunction: () => typeof window !== 'undefined' && !!window.CheckoutWebComponents
  });

  const clearDismissalTimeout = useCallback(() => {
    if (dismissalTimeoutRef.current) {
      clearTimeout(dismissalTimeoutRef.current);
      dismissalTimeoutRef.current = null;
    }
  }, []);

  const clearReadyTimeout = useCallback(() => {
    if (readyTimeoutRef.current) {
      clearTimeout(readyTimeoutRef.current);
      readyTimeoutRef.current = null;
    }
  }, []);

  const handleError = useCallback((error: unknown) => {
    log('error', 'Payment Processing', 'Google Pay error', { error });
    onError({ success: false, errorMessage: error instanceof ApiError ? error.userMessage : undefined });
  }, [log, onError]);

  // Surface a "couldn't open Google Pay" failure from the user's click (e.g. the
  // GP button never loaded). On desktop Safari this is typically a blocked
  // popup, so probe testPopupBlocked() — reliable here as it runs inside the
  // user gesture — and report POPUP_BLOCKED (localised message). Elsewhere, or
  // when not blocked, report a generic failure (MBW-2436).
  const failOnClick = useCallback(() => {
    if (isSafari && !isMobile) {
      testPopupBlocked().then((isBlocked) => {
        if (isBlocked) log('info', 'Google Pay', 'Safari popup blocked');
        onError({
          success: false,
          ...(isBlocked ? { errorType: PAYMENT_ERROR_TYPES.POPUP_BLOCKED } : {})
        });
      });
      return;
    }
    onError({ success: false });
  }, [log, onError]);

  const cleanupFlow = useCallback(() => {
    clearDismissalTimeout();
    clearReadyTimeout();
    flowRef.current?.unmount();
    flowRef.current = null;
    checkoutRef.current = null;
    pollingStartedRef.current = false;
    preparedAmountRef.current = null;
    if (nativeTriggerRef) nativeTriggerRef.current = null;
  }, [clearDismissalTimeout, clearReadyTimeout, nativeTriggerRef]);

  // Google Pay payment flow — purchase + payment session.
  // Runs silently up front (on mount / amount change): it must NOT report
  // 'processing' status, or the parent would navigate to the processing screen
  // before the user interacts (MBW-2436). Prep failures are logged but not
  // surfaced via onError — the button just never becomes ready. Status/errors
  // are reported only once the user taps the GP button and during the poll.
  const processPayment = useCallback(async () => {
    try {
      const createPurchaseResponse = await withCaptchaPromise('checkout', async () => {
        const processingData = await getProcessingData(amount, {
          use_reference_transaction: true,
          with3ds: false,
          with_bonus: withBonus,
          purchase_type: purchaseType?.toLowerCase(),
          context: purchaseContext,
          platform: getClientPlatformForAPI(),
          ...browserContext,
          ...(advisorId && { intended_advisor_id: advisorId }),
          ...(clickSource && { click_source: clickSource }),
          ...(currency && { currency }),
          ...(fxRate && { fx_rate: fxRate })
        });

        // Note: trackPurchaseStarted is fired on the user's click (see the
        // trigger in onReady), not here — the purchase is *prepared* up front but
        // only "started" once the user actually taps the GP button (MBW-2436).
        analytics.setConversionEventId(processingData.conversion_event_id);

        return createPurchase({
          requiredParams: processingData.required_params || {},
          riskModel,
          riskModelConfig,
          fxRate,
          currency
        });
      }, withCaptcha);

      if (createPurchaseResponse.error) {
        log('error', 'Payment Processing', 'Google Pay create purchase error (prep)', {
          errorMessage: createPurchaseResponse.error,
          errorType: createPurchaseResponse.errorType
        });
        const prepError = {
          errorMessage: createPurchaseResponse.error,
          errorType: createPurchaseResponse.errorType,
          spendingLimits: createPurchaseResponse.spendingLimits
        };
        if (nativeTriggerRef) {
          nativeTriggerRef.current = () => onError({ success: false, ...prepError });
        }
        onReady?.();
        return;
      }

      if (!createPurchaseResponse.id) {
        log('error', 'Payment Processing', 'Google Pay prep: missing purchase id');
        return;
      }

      userCreditIdRef.current = createPurchaseResponse.id;

      const paymentSessionConfig = await withCaptchaPromise('checkout', async (rcToken: string) => createPaymentSession(paymentSourceId, createPurchaseResponse.id, rcToken, fxRate, currency), withCaptcha);

      if (!paymentSessionConfig.payment_session?.id) {
        log('error', 'Payment Processing', 'Google Pay prep: missing payment session id');
        return;
      }

      paymentSessionIdRef.current = paymentSessionConfig.payment_session.id;
      setSessionConfig(paymentSessionConfig);
    } catch (error) {
      log('error', 'Payment Processing', 'Google Pay prep error', { error });
    }
  }, [amount, withBonus, paymentSourceId, purchaseType, advisorId, conversationType, clickSource, purchaseContext, riskModel, riskModelConfig, analytics, currency, fxRate, log, withCaptcha, nativeTriggerRef, onReady, onError]);

  const pollPaymentStatus = useCallback(async () => {
    if (pollingStartedRef.current) return;
    pollingStartedRef.current = true;

    // Safety net: once we're polling for the result, the dismissal timer must
    // never fire onDismiss (which would navigate away from the success screen).
    clearDismissalTimeout();

    try {
      const creditId = userCreditIdRef.current!;

      onStatusChange?.('polling', 'Completing payment...');

      const response = await poll({
        pollFunction: () => getPurchaseStatus(
          creditId,
          getClientPlatformForAPI()
        ),
        checkComplete: (res) => !!(res.okMessage || res.errorMessage)
      });

      analytics.setExtraEventParams(response.extraEventParams);

      if (response.errorMessage) {
        log('error', 'Payment Processing', 'Google Pay charge processing error (poll)', {
          errorMessage: response.errorMessage,
          errorType: response.errorType
        });
        onError({
          success: false,
          errorMessage: response.errorMessage,
          errorType: response.errorType,
          errorTitle: response.errorTitle,
          recoverable: response.recoverable,
          retriable: response.retriable,
          errorOrigin: response.errorOrigin,
          errorPathname: response.errorPathname,
          spendingLimits: response.spendingLimits
        });
        return;
      }

      successMessageRef.current = response.okMessage;
      cashbackAmountRef.current = response.cashbackAmount;

      onSuccess({
        success: true,
        userCreditId: creditId,
        okMessage: successMessageRef.current,
        cashbackAmount: cashbackAmountRef.current,
        pathname: response.pathname,
        threeDAuthResult: response.threeDAuthResult
      });
    } catch (error) {
      handleError(error);
    }
  }, [log, handleError, onStatusChange, onError, onSuccess, analytics, clearDismissalTimeout]);

  // ──────────────────────────────────────────────────────────────────────────
  // Prepare the purchase + payment session up front (on mount / amount change),
  // so the Flow GPay button is mounted and ready before the user clicks Pay.
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (scriptError) {
      // Logged, not surfaced — this is pre-interaction prep (the button just
      // never becomes ready). See processPayment's silent error handling.
      log('error', 'Payment Processing', 'Google Pay SDK script failed to load', { error: scriptError });
      return;
    }
    if (!scriptReady) return;

    // Reuse an already-prepared session for the same amount; refresh on change.
    if (preparedAmountRef.current === amount) return;

    if (preparedAmountRef.current !== null) {
      cleanupFlow();
      setSessionConfig(null);
    }
    preparedAmountRef.current = amount;
    processPayment();
  }, [scriptReady, scriptError, amount, cleanupFlow, log, processPayment]);

  // ──────────────────────────────────────────────────────────────────────────
  // Init Checkout.com SDK + Flow once the session is ready
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!sessionConfig || !scriptReady || !containerRef.current || checkoutRef.current) {
      return;
    }

    const initFlow = async () => {
      try {
        const {
          payment_session: paymentSession,
          public_key: publicKey,
          environment
        } = sessionConfig;

        checkoutRef.current = await window.CheckoutWebComponents({
          paymentSession: {
            id: paymentSession.id,
            // eslint-disable-next-line no-underscore-dangle
            _links: paymentSession._links,
            payment_session_token: paymentSession.payment_session_token,
            payment_session_secret: paymentSession.payment_session_secret
          },
          publicKey,
          environment,
          locale: 'en',
          componentOptions: {
            googlepay: {
              buttonType: 'pay',
              buttonColor: 'black'
            }
          },
          appearance: {
            colorBorder: '#fff'
          }
        });

        const flowSessionId = paymentSession.id;

        // Default trigger: surface the error on click. It's replaced by the real
        // "open sheet" trigger in onReady; if onReady never fires (e.g. Safari
        // blocked pay.google.com's pay.js) this default stays, so the watchdog
        // just opens the gate and a click yields a clean error (MBW-2436).
        if (nativeTriggerRef) nativeTriggerRef.current = failOnClick;

        flowRef.current = checkoutRef.current.create('flow', {
          onChange: () => log('info', 'Google Pay', 'flowComponent onChange'),
          onReady: () => {
            log('info', 'Google Pay', 'flowComponent onReady');
            clearReadyTimeout();
            // Expose a synchronous trigger so the parent can open the sheet from
            // inside the user's click handler (within the live gesture window).
            // Opening the sheet also arms dismissal detection: the SDK fires no
            // callback when the user opens then closes the sheet without paying,
            // so we start the timer here and clear it once payment proceeds
            // (onSubmit/onAuthorized/onPaymentCompleted). Without this the
            // processing screen would hang after a silent dismissal (MBW-2436).
            if (nativeTriggerRef) {
              nativeTriggerRef.current = () => {
                const button = containerRef.current?.querySelector(GPAY_BUTTON_SELECTOR) as HTMLButtonElement | null;
                // The Google Pay button failed to render (e.g. Safari blocked
                // pay.google.com's pay.js). Surface the error on the user's click
                // instead of opening a sheet that can't exist (MBW-2436).
                if (!button) {
                  log('error', 'Payment Processing', 'Google Pay button unavailable on click');
                  failOnClick();
                  return;
                }
                // Real user click → the purchase actually starts now.
                analytics.trackPurchaseStarted();
                button.click();
                clearDismissalTimeout();
                dismissalTimeoutRef.current = setTimeout(() => {
                  dismissalTimeoutRef.current = null;
                  log('info', 'Payment Processing', 'Google Pay may have been dismissed');
                  onDismiss?.();
                }, DISMISSAL_TIMEOUT_MS);
              };
            }
            onReady?.();
          },
          onSubmit: () => {
            // User submitted payment in the sheet — the sheet is open and the
            // payment is proceeding, so stop the dismissal watch armed when the
            // sheet opened. (No popup-blocked probe here: by onSubmit the sheet
            // has clearly opened, and probing window.open outside a page gesture
            // false-positives POPUP_BLOCKED. The real blocked case — the button
            // never loads — is handled by the readiness watchdog + failOnClick.)
            log('info', 'Google Pay', 'flowComponent submit');
            clearDismissalTimeout();
            onStatusChange?.('processing', 'Submitting payment...');
          },
          onAuthorized: () => {
            log('info', 'Google Pay', 'flowComponent onAuthorized');
            clearDismissalTimeout();
            onStatusChange?.('processing', 'Processing payment...');
          },
          onPaymentCompleted: async (_self: any, paymentResponse: any) => {
            log('info', 'Google Pay', 'flowComponent payment completed', { paymentResponse });
            // Payment is completing — kill the dismissal timer so it can't fire
            // onDismiss (which would navigate back) once we reach success. Some
            // Flow runs go onSubmit → onPaymentCompleted without onAuthorized, so
            // clearing only in onAuthorized is not enough (MBW-2436).
            clearDismissalTimeout();
            const { id: paymentId } = paymentResponse;
            processPaymentSessionPayment(
              paymentSourceId,
              flowSessionId,
              userCreditIdRef.current!,
              paymentId
            ).then(() => {
              pollPaymentStatus();
            }).catch((error) => {
              handleError(error);
            });
          },
          onError: async (_self: any, error: any) => {
            log('error', 'Payment Processing', 'Google Pay flow error', { error });
            clearDismissalTimeout();
            onError({ success: false });
          }
        });

        flowRef.current.mount(containerRef.current);

        // Watchdog: if the GP button never renders (onReady doesn't fire — e.g.
        // Safari blocked pay.google.com's pay.js), stop the infinite spinner by
        // opening the gate so the button is clickable. The trigger is still the
        // default failOnClick (onReady never replaced it), so a click yields a
        // clean error instead of hanging (MBW-2436). Cleared in onReady.
        clearReadyTimeout();
        readyTimeoutRef.current = setTimeout(() => {
          readyTimeoutRef.current = null;
          log('error', 'Payment Processing', 'Google Pay button failed to load');
          onReady?.();
        }, BUTTON_READY_TIMEOUT_MS);
      } catch (error) {
        handleError(error);
      }
    };

    initFlow();
  }, [log, sessionConfig, scriptReady, paymentSourceId, pollPaymentStatus, handleError, onError, onStatusChange, onDismiss, onReady, nativeTriggerRef, clearDismissalTimeout, clearReadyTimeout, analytics, failOnClick]);

  // Cleanup on unmount
  useEffect(() => () => {
    cleanupFlow();
  }, [cleanupFlow]);

  return <div ref={ containerRef } style={ { display: 'none' } } />;
};
